/**
 * Admin client for email templates — /api/admin-email-templates.php.
 * Copy for the direct-SMTP share sender (testing / super-admin fallback).
 */

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

async function handle<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { message?: string; error?: string }).message || (data as { error?: string }).error || `Request failed (${res.status})`);
  return data as T;
}

export interface EmailTemplate { key: string; locale: string; subject: string; htmlBody: string; textBody: string; isActive: boolean; updatedAt: string; updatedBy: number | null }
export interface EmailTemplateKey { key: string; placeholders: string[]; hasDefaults: string[] }
export interface EmailTemplatesResponse {
  templates: EmailTemplate[];
  keys: EmailTemplateKey[];
  locales: { code: string; name: string; isActive: boolean }[];
  smtpConfigured: boolean;
  sample: Record<string, string>;
}

export async function fetchEmailTemplates(): Promise<EmailTemplatesResponse> {
  const res = await fetch(`${API_BASE}/admin-email-templates.php`, { credentials: 'include' });
  return handle(res);
}

export async function saveEmailTemplate(key: string, locale: string, body: { subject: string; htmlBody: string; textBody: string; isActive: boolean }): Promise<{ template: EmailTemplate }> {
  const res = await fetch(`${API_BASE}/admin-email-templates.php?key=${encodeURIComponent(key)}&locale=${encodeURIComponent(locale)}`, {
    method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  return handle(res);
}

export async function previewEmailTemplate(key: string, locale: string, draft?: { subject: string; htmlBody: string; textBody: string }): Promise<{ subject: string; html: string; text: string }> {
  const res = await fetch(`${API_BASE}/admin-email-templates.php?action=preview`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, locale, ...(draft ?? {}) }),
  });
  return handle(res);
}

export async function resetEmailTemplate(key: string, locale: string): Promise<{ template: EmailTemplate }> {
  const res = await fetch(`${API_BASE}/admin-email-templates.php?action=reset&key=${encodeURIComponent(key)}&locale=${encodeURIComponent(locale)}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, locale }),
  });
  return handle(res);
}

export async function testEmailTemplate(key: string, locale: string, to: string, draft?: { subject: string; htmlBody: string; textBody: string }): Promise<{ ok: boolean; error?: string | null }> {
  const res = await fetch(`${API_BASE}/admin-email-templates.php?action=test`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, locale, to, ...(draft ?? {}) }),
  });
  return (await res.json().catch(() => ({ ok: false, error: 'network' }))) as { ok: boolean; error?: string | null };
}
